// web/src/types/target.ts - Target types

import type { Project } from './project';
import type { VulnerabilitySummary, PaginationInfo } from './common';

export interface Target {
  id: number;
  project_id: number;
  name: string;
  address: string;
  type: 'single' | 'range' | 'cidr' | 'hostname';
  alive_test: boolean;
  created_at: string;
  updated_at: string;
  project?: Project;

  // Asset Classification
  asset_type?: 'server' | 'web_app' | 'database' | 'domain_controller' | 'vpn_gateway' | 'endpoint' | 'network_device' | 'printer' | 'mail_server' | 'file_server' | 'other' | 'unknown';
  environment?: 'production' | 'test' | 'development' | 'lab' | 'unknown';
  internet_exposed?: 'yes' | 'no' | 'unknown';
  business_criticality?: 'critical' | 'high' | 'medium' | 'low' | 'unknown';
  data_sensitivity?: 'none' | 'internal_technical' | 'internal_employee' | 'internal_business' | 'customer_personal' | 'customer_sensitive' | 'unknown';
  zone_vlan?: string;
  tags?: string;

  // Resolution & Status
  resolved_ips?: string;
  hostname?: string;
  is_alive?: boolean;
  last_resolved_at?: string;

  // Network Hardware Info (populated during full scans for private IPs)
  mac_address?: string;
  mac_vendor?: string;

  // Geolocation (populated during scans with geo lookup)
  country?: string;
  country_code?: string;
  city?: string;
  region?: string;
  latitude?: number;
  longitude?: number;
  isp?: string;
  organization?: string;

  // Findings Integration
  highest_severity?: string;
  findings_count?: number;
  last_scanned_at?: string;

  // IP Range association
  ip_range_id?: number;
  ip_range?: {
    id: number;
    name: string;
    range_value: string;
    zone_id?: number;
  };

  // Open ports and services (populated during scans)
  open_ports?: string;
  services?: string;
  service_banners?: string;
  last_alive_at?: string;
  dead_since?: string;

  // Hostnames/aliases (JSON array string)
  hostnames?: string;
}

// Extended interfaces for frontend components
export interface TargetWithProject extends Target {
  project_name: string;
}

// Form types
export interface CreateTargetForm {
  name: string;
  address: string;
  type: 'single' | 'range' | 'cidr' | 'hostname';
  alive_test: boolean;
}

// Target summary interfaces for enhanced target table
export interface TargetSummary {
  target_id: number;
  name: string;
  address: string;
  hostname: string;
  ip: string;
  type: 'single' | 'range' | 'cidr' | 'hostname';
  status: 'alive' | 'dead' | 'pending' | 'skipped';
  open_ports_count: number;
  vulnerabilities: VulnerabilitySummary;
  scan_time_seconds: number;
  last_seen: string;
  services: string[];
  hostnames?: string[];
}

export interface TargetSummaryResponse {
  targets: TargetSummary[];
  pagination: PaginationInfo;
}

export interface TargetFilters {
  status?: 'alive' | 'dead' | 'pending' | 'all';
  min_ports?: number;
  max_ports?: number;
  has_vulnerabilities?: boolean;
  severity_filter?: string[];
  search?: string;
}

export interface TargetSortOptions {
  field: 'name' | 'status' | 'open_ports_count' | 'vulnerabilities.total' | 'scan_time_seconds';
  order: 'asc' | 'desc';
}

// Default values for target summary
export const DEFAULT_TARGET_FILTERS: TargetFilters = {
  status: 'all',
  has_vulnerabilities: false,
  severity_filter: [],
  search: '',
};

// Type guards for target summary
export const isValidTargetStatus = (status: string): status is TargetSummary['status'] => {
  return ['alive', 'dead', 'pending', 'skipped'].includes(status);
};

export const isValidSortField = (field: string): field is TargetSortOptions['field'] => {
  return ['name', 'status', 'open_ports_count', 'vulnerabilities.total', 'scan_time_seconds'].includes(field);
};

// Constants
export const TARGET_TYPES = ['single', 'range', 'cidr', 'hostname'] as const;
